import { useTheme } from '@mui/material';
import useDesignTokens from '../../../../themes/useDesignTokens';
import {
  buttonHeight,
  buttonPadding,
  buttonFontSize,
  buttonLineHeight,
  iconPadding,
} from './buttonDimensions';

const useCommonButtonStyles = ({
  hierarchy,
  size,
}: Readonly<{ hierarchy: string; size: string }>) => {
  const theme = useTheme();
  const tokens = useDesignTokens();

  const getProgressColor = () => {
    switch (hierarchy) {
      case 'primary':
        return tokens.colors.fg.white;
      case 'secondary':
      case 'secondary-color':
      case 'tertiary-color':
      case 'link-color':
        return tokens.colors.fg.highlight.primary;
      default:
        return 'inherit';
    }
  };

  const getDisabledStyles = () => {
    switch (hierarchy) {
      case 'tertiary':
      case 'tertiary-color':
      case 'link':
      case 'link-color':
        return {
          color: `${tokens.colors.text.disabled.primary} !important`,
          pointerEvents: 'none',
          '&::after': {
            content: 'none',
          },
          backgroundColor: 'inherit',
        };
      case 'primary':
      case 'secondary':
      case 'secondary-color':
      default:
        return {
          border: `1px solid ${tokens.colors.border.disabled}`,
          backgroundColor: tokens.colors.bg.disabled,
          pointerEvents: 'none',
          color: `${tokens.colors.text.disabled.primary} !important`,
          fontColor: `${tokens.colors.text.disabled.primary} !important`,
          '&:hover': {
            border: `1px solid ${tokens.colors.border.disabled}`,
            backgroundColor: tokens.colors.bg.disabled,
          },
          '&::after': {
            content: 'none',
          },
        };
    }
  };

  const baseButtonStyles = {
    height: buttonHeight[size],
    minWidth: buttonHeight[size],
    padding: `${theme.sizeGuide.none} ${buttonPadding[size]}`,
    gap: theme.sizeGuide.xs,
    borderRadius: tokens.radius.xs,
    fontSize: buttonFontSize[size],
    boxShadow:
      (hierarchy === 'primary' ||
        hierarchy === 'secondary' ||
        hierarchy === 'secondary-color') &&
      theme.customShadows.buttonShadowXS,
    lineHeight: buttonLineHeight[size],
    '&::after': {
      borderRadius: tokens.radius.xs,
    },
    '&:active::after': {
      borderRadius: tokens.radius.xs,
    },
    '&:disabled': getDisabledStyles(),
  };

  const getDisabledIconStyles = () => {
    switch (hierarchy) {
      case 'tertiary':
        return {
          backgroundColor: 'inherit',
          color: `${tokens.colors.text.disabled.primary} !important`,
          '&::after': {
            content: 'none',
          },
        };
      case 'primary':
      case 'secondary':
      default:
        return {
          backgroundColor: tokens.colors.bg.disabled,
          color: `${tokens.colors.text.disabled.primary} !important`,
          border: `1px solid ${tokens.colors.border.disabled} !important`,
          '&:hover': {
            backgroundColor: tokens.colors.bg.disabled,
            color: `${tokens.colors.text.disabled.primary} !important`,
            border: `1px solid ${tokens.colors.border.disabled} !important`,
          },
          '&:focus': {
            backgroundColor: tokens.colors.bg.disabled,
            color: `${tokens.colors.text.disabled.primary} !important`,
            border: `1px solid ${tokens.colors.border.disabled} !important`,
          },
          '&::after': {
            content: 'none',
          },
        };
    }
  };

  const baseIconStyles = {
    height: buttonHeight[size] ? buttonHeight[size] : 'auto',
    minWidth: buttonHeight[size] ? buttonHeight[size] : 'auto',
    width: 'auto',
    padding: `${iconPadding[size]}`,
    fontSize: buttonFontSize[size],
    gap: theme.sizeGuide.xs,
    boxShadow: `${theme.customShadows.buttonShadowXS} !important`,
    borderRadius: tokens.radius.xs,
    '&::after': {
      borderRadius: tokens.radius.xs,
    },
    '&:active::after': {
      borderRadius: tokens.radius.xs,
    },
  };

  return {
    getProgressColor,
    baseButtonStyles,
    getDisabledStyles,
    getDisabledIconStyles,
    baseIconStyles,
  };
};

export default useCommonButtonStyles;
